import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Select, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { api } from 'services/api';
import avatar from '../../../assets/images/onboarding/first-message-avatar.png';
import { message } from '../../../utils/messageWithScroll';

function normalizePhone(phone) {
	return phone ? String(phone).replace(/[^\d+]/g, '') : '';
}

function getSender(formNumber, number) {
	// O backend acha o canal pelo numero exatamente como foi salvo (na Meta ele vem formatado,
	// "+55 11 9..."), e o registro ativo tem esse formato.
	const saved = (number?.numbers?.actives || []).find(
		(active) =>
			(formNumber?.id && String(active.id) === String(formNumber.id)) ||
			(formNumber?.device_id &&
				active.api === formNumber.api &&
				active.device_id === formNumber.device_id),
	);
	if (saved?.number) {
		return saved.number;
	}

	const configuredNumber = normalizePhone(formNumber?.number);
	const countryCode = String(formNumber?.country_code_dial || '');

	if (configuredNumber && configuredNumber !== countryCode && configuredNumber.length >= 8) {
		return configuredNumber;
	}

	return normalizePhone(number?.numbers?.actives?.[0]?.number);
}

// Sem "+" e com ate 11 digitos o numero e nacional (DDD + numero): ganha o DDI do numero conectado.
function getRecipient(typed, countryCodeDial) {
	const digits = String(typed || '').replace(/\D/g, '');
	const dial = String(countryCodeDial || '').replace(/\D/g, '');
	if (!digits || String(typed).trim().startsWith('+') || digits.length > 11) {
		return digits;
	}
	return dial + digits;
}

// A Meta so aceita template para abrir conversa. O onboarding envia sem preencher nada,
// entao ficam de fora os que pedem variavel ({{1}}) ou midia/localizacao no cabecalho.
function isPlainTemplate(template) {
	if (template.status && template.status !== 'APPROVED') {
		return false;
	}
	return (template.components || []).every(
		(component) =>
			!JSON.stringify(component).includes('{{') &&
			!(component.type === 'HEADER' && component.format && component.format !== 'TEXT'),
	);
}

export default function FirstMessageScreen({
	formNumber,
	number,
	active,
	language,
	onPrevious,
	onComplete,
}) {
	// As chaves first_message_* so existem no react-intl; o `language` (dicionario legado) nao as tem,
	// e por isso a tela saia sempre em portugues.
	const intl = useIntl();
	const t = (id) => intl.formatMessage({ id });
	const [to, setTo] = useState('');
	const [text, setText] = useState(() => t('first_message_default'));
	const [isSending, setIsSending] = useState(false);
	const isMeta = formNumber?.api === 'meta';
	const [templates, setTemplates] = useState([]);
	const [templateIndex, setTemplateIndex] = useState(undefined);
	const [templatesError, setTemplatesError] = useState(false);
	const recipient = getRecipient(to, formNumber?.country_code_dial);
	// O "+" (ou Enter) confirma o numero como destino e ele vira etiqueta; clicar nela volta a editar.
	const [toAdded, setToAdded] = useState(false);
	const canAddTo = to.replace(/\D/g, '').length >= 8;
	const addTo = (event) => {
		// Enter aqui confirma o numero, nao envia o formulario.
		event.preventDefault();
		if (canAddTo) setToAdded(true);
	};
	const editTo = (event) => {
		if (event.type === 'click' || event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			setToAdded(false);
		}
	};

	useEffect(() => {
		if (!isMeta) return;
		api
			.get('getTemplates', { params: { token: formNumber.token, domain: number?.infos?.domain } })
			.then((res) => {
				const list = Array.isArray(res.data) ? res.data.filter(isPlainTemplate) : [];
				setTemplates(list);
				setTemplatesError(!list.length);
			})
			.catch(() => setTemplatesError(true));
	}, [isMeta]);

	const sendMessage = async (event) => {
		event.preventDefault();

		const messageText = text.trim();
		const template = templates[templateIndex];
		const sender = getSender(formNumber, number);

		if (isMeta && !template) {
			message.error(t('first_message_select_template'));
			return;
		}

		if (!isMeta && !messageText) {
			message.error(language?.msg_empty || 'Digite uma mensagem.');
			return;
		}

		if (!sender) {
			message.error(
				language?.add_number_error || 'Não foi possível identificar o número conectado.',
			);
			return;
		}

		setIsSending(true);

		try {
			if (active?.createCustomer) {
				await active.createCustomer('api_whatsapp_enviou_a_primeira_mensagem').catch(() => {});
			}
			// Envio de verdade pelo WhatsApp para o numero digitado. Sem registerOnChat: ele so
			// funciona para destino que veio do CRM, e a conversa entra no Bate-papo quando o
			// numero de teste responder.
			const result = await number.sendMessage(
				isMeta
					? {
							params: [],
							from: sender,
							to: [recipient],
							files: [],
							api: 'meta',
							templateName: template.name,
							templateLanguage: template.language,
							templateComponents: template.components,
							wabaId: template.wabaId || null,
							mobile: false,
							registerOnChat: false,
					  }
					: {
							message: messageText,
							from: sender,
							to: [recipient],
							files: [],
							mobile: false,
							registerOnChat: false,
					  },
			);

			message.success(language?.send_message_success || 'Mensagem enviada.');
			try {
				window.BX24.callMethod('im.notify', {
					to: number?.user?.ID,
					message: t('first_message_notify'),
				});
			} catch (notifyError) {
				// A mensagem ja saiu; sem a notificacao o fluxo segue igual.
				console.error('FirstMessageScreen::im.notify', notifyError);
			}
			onComplete(result?.dialog_id);
		} catch (error) {
			console.error('FirstMessageScreen::sendMessage', error);
			// Sempre a mensagem do Figma: os codigos do backend (FAILED_TO_SEND_TO_DIALOG...) nao sao texto.
			message.error(
				error?.msg === 'ERROR_EMPTY_CHANNEL_ID'
					? t('first_message_channel_error')
					: t('first_message_send_error'),
			);
		} finally {
			setIsSending(false);
		}
	};

	return (
		<div className="first-message-screen">
			<header className="onboarding-screen-header">
				<h1>{t('first_message_title')}</h1>
				<p>{t('first_message_subtitle')}</p>
			</header>

			<form className="first-message-composer" onSubmit={sendMessage}>
				{toAdded ? (
					<div
						role="button"
						tabIndex={0}
						aria-label={`${t('edit')} ${to.trim()}`}
						className="first-message-to-added"
						onClick={editTo}
						onKeyDown={editTo}
					>
						<Tag className="first-message-to-tag">{to.trim()}</Tag>
						<PlusOutlined />
					</div>
				) : (
					<Input
						size="large"
						inputMode="tel"
						autoFocus={Boolean(to)}
						className="first-message-field first-message-to"
						value={to}
						onChange={(event) => setTo(event.target.value)}
						onPressEnter={addTo}
						placeholder={t('first_message_to_placeholder')}
						suffix={
							<PlusOutlined
								role="button"
								aria-label={t('first_message_to_add')}
								className={canAddTo ? 'first-message-to-add' : 'first-message-to-add-disabled'}
								onMouseDown={(event) => event.preventDefault()}
								onClick={addTo}
							/>
						}
					/>
				)}

				<div className="first-message-preview">
					<div className="first-message-preview-bubble-row">
						<img className="first-message-avatar" src={avatar} alt="" />
						<div className="first-message-preview-bubble">{t('first_message_preview')}</div>
					</div>

					<div className="first-message-input-block">
						{isMeta ? (
							<Select
								size="large"
								className="first-message-field"
								value={templateIndex}
								onChange={setTemplateIndex}
								disabled={!templates.length}
								placeholder={t('first_message_select_template')}
							>
								{templates.map((template, index) => (
									<Select.Option key={template.id ?? template.name} value={index}>
										{template.name + ' - ' + (template.languageDescription ?? template.language)}
									</Select.Option>
								))}
							</Select>
						) : (
							<Input
								size="large"
								allowClear
								className="first-message-field"
								value={text}
								onChange={(event) => setText(event.target.value)}
								aria-label={language?.type_your_msg || 'Digite sua mensagem'}
							/>
						)}
						{isMeta && templatesError ? (
							<p className="first-message-error" role="alert">
								{t('first_message_templates_error')}
							</p>
						) : null}
					</div>
				</div>

				<div className="onboarding-actions">
					<Button size="large" className="onboarding-previous" onClick={onPrevious}>
						{language?.previous || 'Anterior'}
					</Button>
					<Button
						size="large"
						type="primary"
						htmlType="submit"
						className="onboarding-next"
						loading={isSending}
						disabled={!toAdded}
					>
						{t('first_message_send')}
					</Button>
				</div>
			</form>
		</div>
	);
}
